import {
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '@/utils/cn';

const BASE = cn(
  'w-full rounded-lg border bg-white/[0.05] backdrop-blur-xl',
  'px-md py-sm text-sm text-text-primary',
  'placeholder:text-text-tertiary focus:outline-none',
  'transition-colors'
);

function borderFor(bad: boolean): string {
  return bad
    ? 'border-error focus:border-error'
    : 'border-white/[0.08] focus:border-brand-red/50';
}

function ErrorText({ error }: { error?: string | undefined }): ReactNode {
  if (!error) return null;
  return <p className="mt-xs text-xs text-error">{error}</p>;
}

function FieldLabel({ label }: { label: string }): ReactNode {
  return (
    <span className="mb-1.5 block text-xs font-medium text-text-secondary">
      {label}
    </span>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  error?: string | undefined;
  invalid?: boolean | undefined;
  label?: string | undefined;
};

export function FieldInput({
  error,
  invalid,
  className,
  label,
  ...props
}: InputProps): ReactNode {
  const bad = invalid || !!error;
  return (
    <label className="block">
      {label && <FieldLabel label={label} />}
      <input
        {...props}
        aria-invalid={bad || undefined}
        className={cn(BASE, borderFor(bad), className)}
      />
      <ErrorText error={error} />
    </label>
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  error?: string | undefined;
  invalid?: boolean | undefined;
};

export function FieldSelect({
  error,
  invalid,
  className,
  children,
  ...props
}: SelectProps): ReactNode {
  const bad = invalid || !!error;
  return (
    <div>
      <select
        {...props}
        aria-invalid={bad || undefined}
        className={cn(BASE, 'appearance-none', borderFor(bad), className)}
      >
        {children}
      </select>
      <ErrorText error={error} />
    </div>
  );
}
